import { useCallback } from 'react';
import { store } from '~/app/redux/index';
import { actions as formViewerActions } from '~/app/redux/modules/formViewer';

/**
 * useSpecialFormLoad
 *
 * Owns the handleSpecialFormLoadResponse handler that was previously a class
 * method on FormViewer. This is called by useSocketMessage (via
 * socketCallbacksRef) when the server sends back a special-form payload.
 *
 * Special forms handled:
 *   retn18 — bookmark field update
 *   retn22 — K-1 manager (form load + modal open)
 *   form12 — add-form list refresh (BA package)
 *   prin12/13/17/18 — remote-sign selection
 *   retn19 — preparer notes update
 *
 * Returns:
 *   { handleSpecialFormLoadResponse }
 */
const useSpecialFormLoad = ({
  calcAPIRef,
  formNavRef,
  // Hook object refs (always-current via ref pattern)
  bookmarks, // useBookmarks result
  print, // usePrint result
  addForm, // useAddForm result
  // Redux action dispatchers (passed from wrapper props)
  toggleK1ManagerModal,
  onStopSpin,
}) => {
  const handleSpecialFormLoadResponse = useCallback(
    data => {
      // if MSGBOX is present, display the message; load form regardless
      if (
        data.calcResponse &&
        data.calcResponse.arg !== undefined &&
        data.calcResponse.arg.MSGBOX !== undefined
      ) {
        store.dispatch(formViewerActions.openMsgDialog(data.calcResponse.arg.MSGBOX));
      }

      // All Binary attachment form names start with "DOX"
      if (data.form.slice(0, 3) === 'DOX') {
        print.displayPrint(data.calcResponse.PDFBlob);
        return;
      }

      const activeForm = data.form;
      switch (data.form) {
        case 'retn18':
          bookmarks.handleBookmarkField(data.calcResponse.arg.bookmarks, false);
          break;
        case 'retn22':
          calcAPIRef.current.ReturnFormLoad('000A00', '', '', '');
          toggleK1ManagerModal(true);
          break;
        case 'form12':
          addForm.updateAddFormList('BA');
          formNavRef.current.navigateToSpecialForm('form12');
          break;
        case 'prin12':
        case 'prin13':
        case 'prin17':
        case 'prin18':
          print.handleRemoteSignSelected(activeForm);
          onStopSpin?.();
          break;
        case 'retn19':
          // If the return is locked, notes will come back empty in this response.
          // We already save the notes to local state from the 'return_init'
          if (data.calcResponse.arg?.notes?.length > 0) {
            bookmarks.handlePreparerNotesReq(data.calcResponse.arg.notes);
          }
          break;
        default:
          formNavRef.current.navigateToSpecialForm(data.form);
          break;
      }
    },
    [calcAPIRef, bookmarks, print, addForm, toggleK1ManagerModal, onStopSpin],
  );

  return { handleSpecialFormLoadResponse };
};

export default useSpecialFormLoad;
